import type {
    NavigationGuard,
    NavigationIntent,
    NavigationResult,
    NavigationTransitionHandler,
    PreparedNavigation,
    WebNavigationHost,
} from '.';
import { signal } from '@preact/signals-core';
import type { Signal } from '@preact/signals-core';
import { Action } from 'history';
import type { History, Update } from 'history';
import { inject, injectable } from 'src/features/ioc';
import { isSameLocation, locationsEqual, toLocation } from 'src/lib/navigation/path';
import type { AppLocation, NavigateOptions, NavigationTarget } from 'src/lib/navigation/types';
import type { ReactBindings } from 'src/types/bindings';

interface GuardRegistration {
    readonly guard: NavigationGuard;
    readonly unsubscribe: () => void;
}

interface PendingTraversal {
    readonly reject: (error: unknown) => void;
    readonly resolve: (result: NavigationResult) => void;
    readonly sequence: number;
    readonly timeout: ReturnType<typeof setTimeout>;
}

interface PendingHistoryCommit {
    readonly prepared: PreparedNavigation | null;
}

type NavigationHistory = Omit<History, 'block'>;

interface PopTransition {
    controller: AbortController | null;
    readonly delta: number;
    readonly fromIndex: number;
    phase: 'preparing' | 'redirecting' | 'restoring' | 'retrying';
    prepared: PreparedNavigation | null;
    readonly targetIndex: number;
    readonly targetLocation: AppLocation;
    timeout: ReturnType<typeof setTimeout> | null;
    readonly traversalSequence: number | null;
}

const traversalNoUpdateTimeoutMs = 250;

@injectable()
export class WebNavigationServiceImpl implements WebNavigationHost, Disposable {
    #activeNavigation: AbortController | null = null;
    readonly #guards = new Set<GuardRegistration>();
    #historyIndex: number | null;
    readonly #lastDashboardLocation = signal<AppLocation | null>(null);
    readonly #location: Signal<AppLocation>;
    readonly #log: ReactBindings['Logger'];
    #navigationSequence = 0;
    #pendingHistoryCommit: PendingHistoryCommit | null = null;
    #pendingTraversal: PendingTraversal | null = null;
    #popTransition: PopTransition | null = null;
    readonly #requiresConfirmation = signal(false);
    #transitionHandler: NavigationTransitionHandler | null = null;
    readonly #unlistenHistory: () => void;
    #warnedAboutUnguardablePop = false;

    get location() {
        return this.#location;
    }

    get lastDashboardLocation() {
        return this.#lastDashboardLocation;
    }

    get requiresConfirmation() {
        return this.#requiresConfirmation;
    }

    constructor(
        @inject('History') private readonly history: NavigationHistory,
        @inject('Logger') logger: ReactBindings['Logger'],
    ) {
        this.#log = logger.getSubLogger({ name: 'WebNavigationService' });
        this.#location = signal(history.location);
        this.#historyIndex = this.#readHistoryIndex();
        this.#updateLastDashboardLocation(history.location);
        this.#unlistenHistory = history.listen((update) => this.#handleHistoryUpdate(update));
    }

    async navigate(to: NavigationTarget | number, options: NavigateOptions = {}): Promise<NavigationResult> {
        this.#supersedePopTransition();
        if (typeof to === 'number') {
            this.#supersedeActiveNavigation();
            return this.#startTraversal(to, this.#navigationSequence);
        }

        const { controller, sequence } = this.#beginNavigation();
        const intent = this.#createIntent(to, options);
        // Navigating to the location already shown re-enters it without adding a history entry.
        const replace = options.replace || isSameLocation(intent.to!, this.history.location);
        if (!(await this.#confirmGuards(intent))) {
            this.#finishNavigation(controller);
            return { status: 'denied' };
        }
        if (sequence !== this.#navigationSequence || controller.signal.aborted) {
            return { status: 'superseded' };
        }

        let prepared: PreparedNavigation | null = null;
        try {
            prepared = this.#transitionHandler
                ? await this.#transitionHandler.prepare(intent.to!, controller.signal)
                : null;
        } catch (error) {
            this.#finishNavigation(controller);
            if (controller.signal.aborted) return { status: 'superseded' };
            throw error;
        }
        if (sequence !== this.#navigationSequence || controller.signal.aborted) {
            prepared?.[Symbol.dispose]();
            return { status: 'superseded' };
        }

        const target = prepared?.location ?? intent.to!;
        this.#commitPreparedNavigation(() => {
            const historyTarget = {
                hash: target.hash,
                pathname: target.pathname,
                search: target.search,
            };
            if (replace || prepared?.replace) {
                this.history.replace(historyTarget, target.state);
            } else {
                this.history.push(historyTarget, target.state);
            }
        }, prepared);
        this.#finishNavigation(controller);
        return { status: 'completed' };
    }

    async canNavigate(to: NavigationTarget | number): Promise<boolean> {
        return this.#confirmGuards(this.#createIntent(to));
    }

    async confirm(kind: Exclude<NavigationIntent['kind'], 'navigate'>): Promise<boolean> {
        return this.#confirmGuards({ kind, to: null });
    }

    registerGuard(guard: NavigationGuard): Disposable {
        const registration: GuardRegistration = {
            guard,
            unsubscribe: guard.requiresConfirmation.subscribe(() => this.#updateRequiresConfirmation()),
        };
        this.#guards.add(registration);
        this.#updateRequiresConfirmation();

        let disposed = false;
        return {
            [Symbol.dispose]: () => {
                if (disposed) return;
                disposed = true;
                registration.unsubscribe();
                this.#guards.delete(registration);
                this.#updateRequiresConfirmation();
            },
        };
    }

    setTransitionHandler(handler: NavigationTransitionHandler): Disposable {
        if (this.#transitionHandler) {
            throw new Error('A navigation transition handler is already registered');
        }
        this.#transitionHandler = handler;

        return {
            [Symbol.dispose]: () => {
                if (this.#transitionHandler !== handler) return;
                this.#supersedePopTransition();
                this.#supersedeActiveNavigation();
                this.#transitionHandler = null;
                this.#pendingHistoryCommit?.prepared?.[Symbol.dispose]();
                this.#pendingHistoryCommit = null;
            },
        };
    }

    #createIntent(to: NavigationTarget | number, options: NavigateOptions = {}): NavigationIntent {
        if (typeof to === 'number') {
            return { kind: 'navigate', to: null };
        }

        return { kind: 'navigate', to: toLocation(to, this.history.location, options.state) };
    }

    async #confirmGuards(intent: NavigationIntent): Promise<boolean> {
        const registrations = [...this.#guards];

        for (const registration of registrations) {
            if (!this.#guards.has(registration)) continue;
            try {
                if ((await registration.guard.confirm(intent)) === 'deny') {
                    return false;
                }
            } catch (error) {
                this.#log.error('Navigation guard failed; navigation denied', { error, intent });
                return false;
            }
        }
        return true;
    }

    #handleHistoryUpdate({ action, location }: Update): void {
        const nextIndex = this.#readHistoryIndex();
        if (action === Action.Pop) {
            this.#handlePopUpdate(location, nextIndex);
            return;
        }

        this.#historyIndex = nextIndex;
        const popTransition = this.#popTransition;
        if (popTransition?.phase === 'redirecting') {
            const prepared = popTransition.prepared;
            this.#popTransition = null;
            this.#publishLocation(location, prepared);
            this.#finishPopTransition(popTransition, { status: 'completed' });
            return;
        }
        if (popTransition) {
            this.#supersedePopTransition();
        }

        const pendingCommit = this.#pendingHistoryCommit;
        this.#pendingHistoryCommit = null;
        if (!pendingCommit) {
            this.#supersedeActiveNavigation();
        }
        this.#publishLocation(location, pendingCommit?.prepared ?? null);
    }

    #handlePopUpdate(location: AppLocation, nextIndex: number | null): void {
        const popTransition = this.#popTransition;
        if (popTransition) {
            this.#handleOwnedPopUpdate(popTransition, location, nextIndex);
            return;
        }

        const previousIndex = this.#historyIndex;
        const pendingTraversal = this.#pendingTraversal;
        const traversalSequence = pendingTraversal?.sequence ?? null;
        if (pendingTraversal) {
            clearTimeout(pendingTraversal.timeout);
        } else {
            this.#supersedeActiveNavigation();
        }

        if (
            previousIndex !== null &&
            nextIndex !== null &&
            previousIndex === nextIndex &&
            locationsEqual(location, this.#location.value)
        ) {
            if (traversalSequence !== null) {
                this.#completePendingTraversal(traversalSequence, { status: 'completed' });
            }
            return;
        }

        this.#historyIndex = nextIndex;
        if (this.#guards.size === 0) {
            // Clean POPs stay committed. Route preparation follows the browser
            // update without a restore/retry cycle, so the address bar does not bounce.
            this.#publishLocation(location);
            void this.#prepareCommittedPop(location, traversalSequence);
            return;
        }

        if (previousIndex === null || nextIndex === null) {
            this.#warnAboutUnguardablePop('Cannot guard browser traversal without indexed history entries', {
                location,
                nextIndex,
                previousIndex,
            });
            this.#publishLocation(location);
            void this.#prepareCommittedPop(location, traversalSequence);
            return;
        }

        const delta = previousIndex - nextIndex;
        if (delta === 0) {
            this.#warnAboutUnguardablePop('Cannot guard browser traversal without a nonzero history delta', {
                location,
                nextIndex,
                previousIndex,
            });
            this.#publishLocation(location);
            void this.#prepareCommittedPop(location, traversalSequence);
            return;
        }

        this.#startGuardedPopTransition(previousIndex, nextIndex, location, traversalSequence);
    }

    #handleOwnedPopUpdate(transition: PopTransition, location: AppLocation, nextIndex: number | null): void {
        this.#clearPopTransitionWatchdog(transition);
        if (transition.phase === 'restoring' && nextIndex === transition.fromIndex) {
            this.#historyIndex = nextIndex;
            transition.phase = 'preparing';
            void this.#prepareRestoredPop(transition);
            return;
        }

        if (transition.phase === 'retrying' && nextIndex === transition.targetIndex) {
            this.#historyIndex = nextIndex;
            const prepared = transition.prepared;
            const target = prepared?.location ?? transition.targetLocation;
            if (prepared && (!locationsEqual(target, transition.targetLocation) || prepared.replace)) {
                transition.phase = 'redirecting';
                this.history.replace(
                    { hash: target.hash, pathname: target.pathname, search: target.search },
                    target.state,
                );
                return;
            }

            this.#popTransition = null;
            this.#publishLocation(location, prepared);
            this.#finishPopTransition(transition, { status: 'completed' });
            return;
        }

        this.#restartInterruptedPopTransition(transition, location, nextIndex);
    }

    #startGuardedPopTransition(
        fromIndex: number,
        targetIndex: number,
        targetLocation: AppLocation,
        traversalSequence: number | null,
    ): void {
        const transition: PopTransition = {
            controller: null,
            delta: fromIndex - targetIndex,
            fromIndex,
            phase: 'restoring',
            prepared: null,
            targetIndex,
            targetLocation,
            timeout: null,
            traversalSequence,
        };
        this.#popTransition = transition;

        // A POP has already selected the target entry. Restore the exact prior
        // index before awaiting guards, then retry the inverse delta on allow.
        this.#goForPopTransition(transition, 'restoring', transition.delta);
    }

    #restartInterruptedPopTransition(transition: PopTransition, location: AppLocation, nextIndex: number | null): void {
        const fromIndex = transition.fromIndex;
        this.#supersedePopTransition();
        this.#historyIndex = nextIndex;

        if (nextIndex === fromIndex) {
            if (locationsEqual(location, this.#location.value)) return;
            this.#warnAboutUnguardablePop(
                'Cannot guard interrupted browser traversal without a nonzero history delta',
                {
                    location,
                    previousIndex: fromIndex,
                },
            );
            this.#publishLocation(location);
            void this.#prepareCommittedPop(location, null);
            return;
        }
        if (this.#guards.size === 0 || nextIndex === null) {
            if (nextIndex === null) {
                this.#warnAboutUnguardablePop(
                    'Cannot guard interrupted browser traversal without an indexed history entry',
                    {
                        location,
                        previousIndex: fromIndex,
                    },
                );
            }
            this.#publishLocation(location);
            void this.#prepareCommittedPop(location, null);
            return;
        }

        this.#startGuardedPopTransition(fromIndex, nextIndex, location, null);
    }

    #warnAboutUnguardablePop(message: string, context: Record<string, unknown>): void {
        if (this.#warnedAboutUnguardablePop) return;
        this.#warnedAboutUnguardablePop = true;
        this.#log.warn(message, context);
    }

    #goForPopTransition(transition: PopTransition, phase: 'restoring' | 'retrying', delta: number): void {
        this.#clearPopTransitionWatchdog(transition);
        transition.phase = phase;
        transition.timeout = setTimeout(() => {
            if (this.#popTransition !== transition || transition.phase !== phase) return;
            this.#failPopTransition(
                transition,
                new Error(`Browser history ${phase === 'restoring' ? 'restore' : 'retry'} produced no POP update`),
            );
        }, traversalNoUpdateTimeoutMs);
        this.history.go(delta);
    }

    #clearPopTransitionWatchdog(transition: PopTransition): void {
        if (transition.timeout === null) return;
        clearTimeout(transition.timeout);
        transition.timeout = null;
    }

    async #prepareRestoredPop(transition: PopTransition): Promise<void> {
        const { controller, sequence } = this.#beginNavigation(false);
        transition.controller = controller;
        const intent: NavigationIntent = { kind: 'navigate', to: transition.targetLocation };
        if (!(await this.#confirmGuards(intent))) {
            if (this.#popTransition !== transition) return;
            if (sequence !== this.#navigationSequence || controller.signal.aborted) {
                this.#supersedePopTransition();
                return;
            }
            this.#popTransition = null;
            this.#finishPopTransition(transition, { status: 'denied' });
            return;
        }
        if (this.#popTransition !== transition) {
            return;
        }
        if (sequence !== this.#navigationSequence || controller.signal.aborted) {
            this.#supersedePopTransition();
            return;
        }

        let prepared: PreparedNavigation | null = null;
        try {
            prepared = this.#transitionHandler
                ? await this.#transitionHandler.prepare(transition.targetLocation, controller.signal)
                : null;
        } catch (error) {
            if (this.#popTransition !== transition) return;
            if (controller.signal.aborted) {
                this.#supersedePopTransition();
                return;
            }
            this.#failPopTransition(transition, error);
            return;
        }
        if (this.#popTransition !== transition) {
            prepared?.[Symbol.dispose]();
            return;
        }
        if (sequence !== this.#navigationSequence || controller.signal.aborted) {
            transition.prepared = prepared;
            this.#supersedePopTransition();
            return;
        }

        transition.prepared = prepared;
        this.#goForPopTransition(transition, 'retrying', -transition.delta);
    }

    async #prepareCommittedPop(location: AppLocation, traversalSequence: number | null): Promise<void> {
        const transitionHandler = this.#transitionHandler;
        if (!transitionHandler) {
            if (traversalSequence !== null) {
                this.#completePendingTraversal(traversalSequence, { status: 'completed' });
            }
            return;
        }

        const { controller, sequence } = this.#beginNavigation(false);

        let prepared: PreparedNavigation | null = null;
        try {
            prepared = await transitionHandler.prepare(location, controller.signal);
        } catch (error) {
            this.#finishNavigation(controller);
            if (controller.signal.aborted) {
                if (traversalSequence !== null) {
                    this.#completePendingTraversal(traversalSequence, { status: 'superseded' });
                }
                return;
            }
            if (traversalSequence !== null) {
                this.#rejectPendingTraversal(traversalSequence, error);
            } else {
                this.#log.error('Navigation transition failed', { error });
            }
            return;
        }
        if (sequence !== this.#navigationSequence || controller.signal.aborted) {
            prepared?.[Symbol.dispose]();
            this.#finishNavigation(controller);
            if (traversalSequence !== null) {
                this.#completePendingTraversal(traversalSequence, { status: 'superseded' });
            }
            return;
        }

        const target = prepared?.location ?? location;
        if (prepared && (!locationsEqual(target, location) || prepared.replace)) {
            this.#commitPreparedNavigation(
                () =>
                    this.history.replace(
                        { hash: target.hash, pathname: target.pathname, search: target.search },
                        target.state,
                    ),
                prepared,
            );
        } else {
            prepared?.commit(location);
        }
        this.#finishNavigation(controller);
        if (traversalSequence !== null) {
            this.#completePendingTraversal(traversalSequence, { status: 'completed' });
        }
    }

    #commitPreparedNavigation(commit: () => void, prepared: PreparedNavigation | null): void {
        this.#pendingHistoryCommit?.prepared?.[Symbol.dispose]();
        this.#pendingHistoryCommit = { prepared };
        commit();
    }

    #publishLocation(location: AppLocation, prepared: PreparedNavigation | null = null): void {
        this.#location.value = location;
        this.#updateLastDashboardLocation(location);
        prepared?.commit(location);
    }

    #finishPopTransition(transition: PopTransition, result: NavigationResult): void {
        this.#clearPopTransitionWatchdog(transition);
        if (transition.controller) {
            this.#finishNavigation(transition.controller);
        }
        if (transition.traversalSequence !== null) {
            this.#completePendingTraversal(transition.traversalSequence, result);
        }
    }

    #failPopTransition(transition: PopTransition, error: unknown): void {
        if (this.#popTransition === transition) {
            this.#popTransition = null;
        }
        this.#clearPopTransitionWatchdog(transition);
        transition.controller?.abort();
        transition.prepared?.[Symbol.dispose]();
        if (transition.controller) {
            this.#finishNavigation(transition.controller);
        }
        if (transition.traversalSequence !== null) {
            this.#rejectPendingTraversal(transition.traversalSequence, error);
        } else {
            this.#log.error('Navigation transition failed', { error });
        }
    }

    #supersedePopTransition(): void {
        const transition = this.#popTransition;
        if (!transition) return;

        this.#popTransition = null;
        this.#clearPopTransitionWatchdog(transition);
        transition.controller?.abort();
        transition.prepared?.[Symbol.dispose]();
        if (transition.controller) {
            this.#finishNavigation(transition.controller);
        }
        if (transition.traversalSequence !== null) {
            this.#completePendingTraversal(transition.traversalSequence, { status: 'superseded' });
        }
    }

    #readHistoryIndex(): number | null {
        // Memory history exposes its index directly. Browser history v5 stores
        // the equivalent index in native history state for every app entry.
        if ('index' in this.history && typeof this.history.index === 'number') {
            return this.history.index;
        }
        if (typeof window === 'undefined') return null;

        const state: unknown = window.history.state;
        if (typeof state !== 'object' || state === null || !('idx' in state) || typeof state.idx !== 'number') {
            return null;
        }
        return state.idx;
    }

    #startTraversal(delta: number, sequence: number): Promise<NavigationResult> {
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.#completePendingTraversal(sequence, { status: 'completed' });
            }, traversalNoUpdateTimeoutMs);
            this.#pendingTraversal = { reject, resolve, sequence, timeout };
            this.history.go(delta);
        });
    }

    #completePendingTraversal(sequence: number, result: NavigationResult): void {
        const pendingTraversal = this.#pendingTraversal;
        if (!pendingTraversal || pendingTraversal.sequence !== sequence) return;

        clearTimeout(pendingTraversal.timeout);
        this.#pendingTraversal = null;
        pendingTraversal.resolve(result);
    }

    #rejectPendingTraversal(sequence: number, error: unknown): void {
        const pendingTraversal = this.#pendingTraversal;
        if (!pendingTraversal || pendingTraversal.sequence !== sequence) return;

        clearTimeout(pendingTraversal.timeout);
        this.#pendingTraversal = null;
        pendingTraversal.reject(error);
    }

    #supersedePendingTraversal(): void {
        const pendingTraversal = this.#pendingTraversal;
        if (!pendingTraversal) return;
        this.#completePendingTraversal(pendingTraversal.sequence, { status: 'superseded' });
    }

    #beginNavigation(supersedeTraversal = true): { readonly controller: AbortController; readonly sequence: number } {
        this.#activeNavigation?.abort();
        if (supersedeTraversal) {
            this.#supersedePendingTraversal();
        }
        const controller = new AbortController();
        this.#activeNavigation = controller;
        return { controller, sequence: ++this.#navigationSequence };
    }

    #finishNavigation(controller: AbortController): void {
        if (this.#activeNavigation === controller) {
            this.#activeNavigation = null;
        }
    }

    #supersedeActiveNavigation(): void {
        this.#activeNavigation?.abort();
        this.#activeNavigation = null;
        this.#navigationSequence += 1;
        this.#supersedePendingTraversal();
    }

    #updateRequiresConfirmation(): void {
        this.#requiresConfirmation.value = [...this.#guards].some(({ guard }) => guard.requiresConfirmation.value);
    }

    #updateLastDashboardLocation(location: AppLocation): void {
        if (!location.pathname.startsWith('/dashboard/')) return;
        if (!location.pathname.split('/')[2]) return;
        // State is part of what the dashboard showed (how a new workspace was created), so keep it.
        this.#lastDashboardLocation.value = location;
    }

    [Symbol.dispose](): void {
        this.#supersedePopTransition();
        this.#supersedeActiveNavigation();
        this.#pendingHistoryCommit?.prepared?.[Symbol.dispose]();
        this.#pendingHistoryCommit = null;
        this.#transitionHandler = null;
        this.#unlistenHistory();
        for (const registration of this.#guards) {
            registration.unsubscribe();
        }
        this.#guards.clear();
        this.#requiresConfirmation.value = false;
    }
}
